<template>
    <div class="el-table"
      :class="[{
        'header-body-fixed' : isFixedBodyHeader,
        'el-table--fit': fit,
        'el-table--striped': stripe,
        'el-table--border': border || isGroup,
        'el-table--hidden': isHidden,
        'el-table--group': isGroup,
        'el-table--fluid-height': maxHeight,
        'el-table--scrollable-x': layout.scrollX,
        'el-table--scrollable-y': layout.scrollY,
        'el-table--enable-row-hover': !store.states.isComplex,
        'el-table--enable-row-transition': (store.states.data || []).length !== 0 && (store.states.data || []).length < 100
      }, tableSize ? `el-table--${ tableSize }` : '']"
      ref="tableContainer"
      @mouseleave="handleMouseLeave($event)">
      <div class="hidden-columns" ref="hiddenColumns"><slot></slot></div>
      <div
        v-if="showHeader"
        v-mousewheel="handleHeaderFooterMousewheel"
        class="el-table__header-wrapper"
        :style="{
            width: tableContainerWidth
        }"
        ref="headerWrapper">
        <table-header
          ref="tableHeader"
          :store="store"
          :border="border"
          :default-sort="defaultSort"
          :style="{
            width: layout.bodyWidth ? layout.bodyWidth + 'px' : ''
          }"
        >
        </table-header>
      </div>
      <aw-smooth-scrollbar 
        :style="[bodyHeight]" 
        :scroll-x="layout.scrollX" 
        :scroll-y="layout.scrollY"
        @scroll="onSmoothScroll" 
        :drag-scroll-dir="dragScrollDir">
      <div
        class="el-table__body-wrapper"
        ref="bodyWrapper"
        :class="[layout.scrollX ? `is-scrolling-${scrollPosition}` : 'is-scrolling-none']"
        :style="[bodyHeight]">
        <table-body
          :context="context"
          :store="store"
          :stripe="stripe"
          :row-class-name="rowClassName"
          :row-style="rowStyle"
          :highlight="highlightCurrentRow"
          :style="{
             width: bodyWidth
          }">
        </table-body>
        <div
          v-if="!data || data.length === 0"
          class="el-table__empty-block"
          ref="emptyBlock"
          :style="emptyBlockStyle">
          <span class="el-table__empty-text" >
            <slot name="empty">{{ emptyText || t('el.table.emptyText') }}</slot>
          </span>
        </div>
        <div
          v-if="$slots.append"
          class="el-table__append-wrapper"
          ref="appendWrapper">
          <slot name="append"></slot>
        </div>
      </div>
      </aw-smooth-scrollbar >
      <div
        v-if="showSummary"
        v-show="data && data.length > 0"
        v-mousewheel="handleHeaderFooterMousewheel"
        class="el-table__footer-wrapper"
        ref="footerWrapper">
        <table-footer
          :store="store"
          :border="border"
          :sum-text="sumText || t('el.table.sumText')"
          :summary-method="summaryMethod"
          :default-sort="defaultSort"
          :style="{
            width: layout.bodyWidth ? layout.bodyWidth + 'px' : ''
          }">
        </table-footer>
      </div>
      <div
        v-if="fixedColumns.length > 0"
        v-mousewheel="handleFixedMousewheel"
        class="el-table__fixed"
        ref="fixedWrapper"
        :style="[{
          width: layout.fixedWidth ? layout.fixedWidth + 'px' : ''
        },
        fixedHeight]">
        <div
          v-if="showHeader"
          class="el-table__fixed-header-wrapper"
          :style="{
              width: layout.fixedWidth ? layout.fixedWidth + 'px' : ''
          }"    
          ref="fixedHeaderWrapper" >
          <table-header
            ref="fixedTableHeader"
            fixed="left"
            :border="border"
            :store="store"
            :style="{
              width: bodyWidth
            }"></table-header>
        </div>
        <div
          class="el-table__fixed-body-wrapper"
          ref="fixedBodyWrapper"
          :style="[{
            top: layout.headerHeight + 'px',
          },
          fixedBodyHeight]">
          <table-body
            fixed="left"
            :store="store"
            :stripe="stripe"
            :highlight="highlightCurrentRow"
            :row-class-name="rowClassName"
            :row-style="rowStyle"
            :style="{
              width: bodyWidth
            }">
          </table-body>
          <div
            v-if="$slots.append"
            class="el-table__append-gutter"
            :style="{ height: layout.appendHeight + 'px'}"></div>
        </div>
        <div
          v-if="showSummary"
          v-show="data && data.length > 0"
          class="el-table__fixed-footer-wrapper"
          ref="fixedFooterWrapper">
          <table-footer
            fixed="left"
            :border="border"
            :sum-text="sumText || t('el.table.sumText')"
            :summary-method="summaryMethod"
            :store="store"
            :style="{
              width: bodyWidth
            }"></table-footer>
        </div>
      </div>
      <div
        v-if="rightFixedColumns.length > 0"
        v-mousewheel="handleFixedMousewheel"
        class="el-table__fixed-right"
        ref="rightFixedWrapper"
        :style="[{
          width: layout.rightFixedWidth ? layout.rightFixedWidth + 'px' : '',
          right: layout.scrollY ? (border ? layout.gutterWidth : (layout.gutterWidth || 0)) + 'px' : ''
        },
        fixedHeight]">
        <div v-if="showHeader"
          class="el-table__fixed-header-wrapper"
          :style="[{
            width: layout.rightFixedWidth ? layout.rightFixedWidth + 'px' : '',
            height: layout.headerHeight + 'px'
          }]"
          ref="rightFixedHeaderWrapper">
          <table-header
            ref="rightFixedTableHeader"
            fixed="right"
            :border="border"
            :store="store"
            :style="{
              width: bodyWidth
            }"></table-header>
        </div>
        <div
          class="el-table__fixed-body-wrapper"
          ref="rightFixedBodyWrapper"
          :style="[{
            top: layout.headerHeight + 'px'
          },
          fixedBodyHeight]">
          <table-body
            fixed="right"
            :store="store"
            :stripe="stripe"
            :row-class-name="rowClassName"
            :row-style="rowStyle"
            :highlight="highlightCurrentRow"
            :style="{
              width: bodyWidth
            }">
          </table-body>
           <div
            v-if="$slots.append"
            class="el-table__append-gutter"
            :style="{ height: layout.appendHeight + 'px' }"></div>
        </div>
        <div
          v-if="showSummary"
          v-show="data && data.length > 0"
          class="el-table__fixed-footer-wrapper"
          ref="rightFixedFooterWrapper">
          <table-footer
            fixed="right"
            :border="border"
            :sum-text="sumText || t('el.table.sumText')"
            :summary-method="summaryMethod"
            :store="store"
            :style="{
              width: bodyWidth
            }"></table-footer>
        </div>
      </div>
      <div
        v-if="rightFixedColumns.length > 0"
        class="el-table__fixed-right-patch"
        ref="rightFixedPatch"
        :style="{
          width: layout.scrollY ? layout.gutterWidth + 'px' : '0',
          height: layout.headerHeight + 'px'
        }"></div>
      <div class="el-table__column-resize-proxy" ref="resizeProxy" v-show="resizeProxyVisible"></div>
    </div>
  </template>
  
  <script type="text/babel">
    import ElCheckbox from 'element-ui/packages/checkbox';
    import { debounce, throttle } from 'throttle-debounce';
    import { addResizeListener, removeResizeListener } from 'element-ui/src/utils/resize-event';
    import Mousewheel from 'element-ui/src/directives/mousewheel';
    // import Locale from 'element-ui/src/mixins/locale';
    import Locale from './locale-mixin';
    import Migrating from 'element-ui/src/mixins/migrating';
    import { createStore, mapStates } from 'element-ui/packages/table/src/store/helper';
    import TableLayout from 'element-ui/packages/table/src/table-layout';
    import TableBody from './table-body';
    import TableHeader from 'element-ui/packages/table/src/table-header';
    import TableFooter from 'element-ui/packages/table/src/table-footer';
    import { parseHeight } from 'element-ui/packages/table/src/util';
    import AwSmoothScrollbar from '@/components/UIComponents/scroll-bar/AwSmoothScrollbar.vue'

    let tableIdSeed = 1;
  
    export default {
      name: 'AwTable',
  
      mixins: [Locale, Migrating],
  
      directives: {
        Mousewheel,
      },
  
      props: {
        data: {
          type: Array,
          default: function() {
            return [];
          }
        },
  
        /* 드래그로 스크롤시 스크롤 허용 방향  x|all */
        dragScrollDir: String,

        /* 헤더를 바디상단으로 고정 옵션 (옵션에 높이(height)가 있어서 박스 영역스크롤일 경우는 무시된다.)*/
        fixedBodyHeader: {
          type: Boolean,
          default: false,
        },

        size: String,
  
        width: [String, Number],
  
        height: [String, Number],
  
        maxHeight: [String, Number],
  
        fit: {
          type: Boolean,
          default: true
        },
  
        stripe: Boolean,
  
        border: Boolean,
  
        rowKey: [String, Function],
  
        context: {},
  
        showHeader: {
          type: Boolean,
          default: true
        },
  
        showSummary: Boolean,
  
        sumText: String,
  
        summaryMethod: Function,
  
        rowClassName: [String, Function],
  
        rowStyle: [Object, Function],
  
        cellClassName: [String, Function],
  
        cellStyle: [Object, Function],
  
        headerRowClassName: [String, Function],
  
        headerRowStyle: [Object, Function],
  
        headerCellClassName: [String, Function],
  
        headerCellStyle: [Object, Function],
  
        highlightCurrentRow: Boolean,
  
        currentRowKey: [String, Number],
  
        emptyText: String,
  
        expandRowKeys: Array,
  
        defaultExpandAll: Boolean,
  
        defaultSort: Object,
  
        tooltipEffect: String,
  
        spanMethod: Function,
  
        selectOnIndeterminate: {
          type: Boolean,
          default: true
        },
  
        indent: {
          type: Number,
          default: 16
        },
  
        treeProps: {
          type: Object,
          default() {
            return {
              hasChildren: 'hasChildren',
              children: 'children'
            };
          }
        },
  
        lazy: Boolean,
  
        load: Function
      },
  
      components: {
        TableHeader,
        TableFooter,
        TableBody,
        ElCheckbox,
        AwSmoothScrollbar,
      },
  
      methods: {

        // 바디 상단 고정 헤더 체크
        checkFixedBodyHeader() {

          // 전체 테이블 크기
          this.tableContainerWidth = this.$tableContainer.width()+'px'
          
          if(!this.canFixedBodyHeader) {
            this.isFixedBodyHeader = false
            return
          }  

          this.isFixedBodyHeader = (window.scrollY - ($(this.$refs.bodyWrapper).offset()?.top || 0)> 0)


        },

        // 바디 상단 고정 헤더 체크 바인딩 시작 
        startFixedBodyHeader() {

          this.$tableContainer =  $(this.$refs.tableContainer)
          this.checkFixedBodyHeader()
          $(window).on('scroll resize',this.checkFixedBodyHeader)

        },
        // smooth 스크롤에서 받은 이벤트로 헤더와 테이블바디 위치를 조정 
        onSmoothScroll( status ) {
            // 헤더 x 위치  조정
            this.$refs.tableHeader.$el.style.transform = `translate3d(-${status.offset.x}px, 0px, 0px)`
            this.awScrollInfo = {
                scrollTop: status.offset.y,
            }
            this.onScroll( status )
        },
        getMigratingConfig() {
          return {
            events: {
              expand: 'expand is renamed to expand-change'
            }
          };
        },
  
        setCurrentRow(row) {
          this.store.commit('setCurrentRow', row);
        },
  
        toggleRowSelection(row, selected) {
          this.store.toggleRowSelection(row, selected, false);
          this.store.updateAllSelected();
        },
  
        toggleRowExpansion(row, expanded) {
          this.store.toggleRowExpansionAdapter(row, expanded);
        },
  
        clearSelection() {
          this.store.clearSelection();
        },
  
        clearFilter(columnKeys) {
          this.store.clearFilter(columnKeys);
        },
  
        clearSort() {
          this.store.clearSort();
        },
  
        handleMouseLeave() {
          this.store.commit('setHoverRow', null);
          if (this.hoverState) this.hoverState = null;
        },
  
        updateScrollY() {
          const changed = this.layout.updateScrollY();
          if (changed) {
            this.layout.notifyObservers('scrollable');
            this.layout.updateColumnsWidth();
          }
        },
  
        handleFixedMousewheel(event, data) {
          const bodyWrapper = this.bodyWrapper;
          if (Math.abs(data.spinY) > 0) {
            const currentScrollTop = bodyWrapper.scrollTop;
            if (data.pixelY < 0 && currentScrollTop !== 0) {
              event.preventDefault();
            }
            if (data.pixelY > 0 && bodyWrapper.scrollHeight - bodyWrapper.clientHeight > currentScrollTop) {
              event.preventDefault();
            }
            bodyWrapper.scrollTop += Math.ceil(data.pixelY / 5);
          } else {
            bodyWrapper.scrollLeft += Math.ceil(data.pixelX / 5);
          }
        },
  
        handleHeaderFooterMousewheel(event, data) {
          const { pixelX, pixelY } = data;
          if (Math.abs(pixelX) >= Math.abs(pixelY)) {
            this.bodyWrapper.scrollLeft += data.pixelX / 5;
          }
        },
  
        // TODO 使用 CSS transform
        syncPostion() {
          const { offsetWidth, scrollWidth, scrollLeft } = this.bodyWrapper;
          const { scrollTop } = this.awScrollInfo || this.bodyWrapper
          
          const { headerWrapper, footerWrapper, fixedBodyWrapper, rightFixedBodyWrapper } = this.$refs;
          if (headerWrapper) headerWrapper.scrollLeft = scrollLeft;
          if (footerWrapper) footerWrapper.scrollLeft = scrollLeft;
          if (fixedBodyWrapper) fixedBodyWrapper.scrollTop = scrollTop;
          if (rightFixedBodyWrapper) rightFixedBodyWrapper.scrollTop = scrollTop;
          const maxScrollLeftPosition = scrollWidth - offsetWidth - 1;
          if (scrollLeft >= maxScrollLeftPosition) {
            this.scrollPosition = 'right';
          } else if (scrollLeft === 0) {
            this.scrollPosition = 'left';
          } else {
            this.scrollPosition = 'middle';
          }
        },
  
        throttleSyncPostion: throttle(16, function() {
          this.syncPostion();
        }),
  
        onScroll(evt) {
          let raf = window.requestAnimationFrame;
          if (!raf) {
            this.throttleSyncPostion();
          } else {
            raf(this.syncPostion);
          }
        },
  
        bindEvents() {
          this.bodyWrapper.addEventListener('scroll', this.onScroll, { passive: true });
          if (this.fit) {
            addResizeListener(this.$el, this.resizeListener);
          }
        },
  
        unbindEvents() {
          this.bodyWrapper.removeEventListener('scroll', this.onScroll, { passive: true });
          if (this.fit) {
            removeResizeListener(this.$el, this.resizeListener);
          }
        },
  
        resizeListener() {
          if (!this.$ready) return;
          let shouldUpdateLayout = false;
          const el = this.$el;
          const { width: oldWidth, height: oldHeight } = this.resizeState;
  
          const width = el.offsetWidth;
          if (oldWidth !== width) {
            shouldUpdateLayout = true;
          }
  
          const height = el.offsetHeight;
          if ((this.height || this.shouldUpdateHeight) && oldHeight !== height) {
            shouldUpdateLayout = true;
          }
  
          if (shouldUpdateLayout) {
            this.resizeState.width = width;
            this.resizeState.height = height;
            this.doLayout();
          }
        },
  
        doLayout() {
          if (this.shouldUpdateHeight) {
            this.layout.updateElsHeight();
          }
          this.layout.updateColumnsWidth();
        },
  
        sort(prop, order) {
          this.store.commit('sort', { prop, order });
        },
  
        toggleAllSelection() {
          this.store.commit('toggleAllSelection');
        }
  
      },
  
      computed: {
        /* 바디상단 헤더 고정 가능 여부 */
        canFixedBodyHeader() {
          // 높이가 없는 자동 높이이고 옵션에 fixedBodyHeader 가 존재
          return !this.layout.scrollY && this.fixedBodyHeader
        },

        tableSize() {
          return this.size || (this.$ELEMENT || {}).size;
        },
  
        bodyWrapper() {
          return this.$refs.bodyWrapper;
        },
  
        shouldUpdateHeight() {
          return this.height ||
            this.maxHeight ||
            this.fixedColumns.length > 0 ||
            this.rightFixedColumns.length > 0;
        },

        /*
        bodyWidth() {
          const { bodyWidth, scrollY, gutterWidth } = this.layout;
          return bodyWidth ? bodyWidth - (scrollY ? gutterWidth : 0) + 'px' : '';
        },
        */
        bodyWidth() {
          const { bodyWidth, scrollY, gutterWidth } = this.layout;
          // 스크롤 영역만큼 줄어 있는것을 꽉차게 한다.
          return bodyWidth ? bodyWidth + 'px' : '';
        },
        layoutBodyWidth() {

        },
  
        bodyHeight() {
          const { headerHeight = 0, bodyHeight, footerHeight = 0} = this.layout;
          if (this.height) {
            return {
              height: bodyHeight ? bodyHeight + 'px' : ''
            };
          } else if (this.maxHeight) {
            const maxHeight = parseHeight(this.maxHeight);
            if (typeof maxHeight === 'number') {
              return {
                'max-height': (maxHeight - footerHeight - (this.showHeader ? headerHeight : 0)) + 'px'
              };
            }
          }
          return {};
        },
  
        fixedBodyHeight() {
          if (this.height) {
            return {
              height: this.layout.fixedBodyHeight ? this.layout.fixedBodyHeight + 'px' : ''
            };
          } else if (this.maxHeight) {
            let maxHeight = parseHeight(this.maxHeight);
            if (typeof maxHeight === 'number') {
              maxHeight = this.layout.scrollX ? maxHeight - this.layout.gutterWidth : maxHeight;
              if (this.showHeader) {
                maxHeight -= this.layout.headerHeight;
              }
              maxHeight -= this.layout.footerHeight;
              return {
                'max-height': maxHeight + 'px'
              };
            }
          }
          return {};
        },
  
        fixedHeight() {
          if (this.maxHeight) {
            if (this.showSummary) {
              return {
                bottom: 0
              };
            }
            return {
              bottom: (this.layout.scrollX && this.data.length) ? this.layout.gutterWidth + 'px' : ''
            };
          } else {
            if (this.showSummary) {
              return {
                height: this.layout.tableHeight ? this.layout.tableHeight + 'px' : ''
              };
            }
            return {
              height: this.layout.viewportHeight ? this.layout.viewportHeight + 'px' : ''
            };
          }
        },
  
        emptyBlockStyle() {
          if (this.data && this.data.length) return null;
          let height = '100%';
          if (this.layout.appendHeight) {
            height = `calc(100% - ${this.layout.appendHeight}px)`;
          }
          return {
            width: this.bodyWidth,
            height
          };
        },
  
        ...mapStates({
          selection: 'selection',
          columns: 'columns',
          tableData: 'data',
          fixedColumns: 'fixedColumns',
          rightFixedColumns: 'rightFixedColumns'
        })
      },
  
      watch: {
        height: {
          immediate: true,
          handler(value) {
            this.layout.setHeight(value);
          }
        },
  
        maxHeight: {
          immediate: true,
          handler(value) {
            this.layout.setMaxHeight(value);
          }
        },
  
        currentRowKey: {
          immediate: true,
          handler(value) {
            if (!this.rowKey) return;
            this.store.setCurrentRowKey(value);
          }
        },
  
        data: {
          immediate: true,
          handler(value) {
            this.store.commit('setData', value);
          }
        },
  
        expandRowKeys: {
          immediate: true,
          handler(newVal) {
            if (newVal) {
              this.store.setExpandRowKeysAdapter(newVal);
            }
          }
        }
      },
  
      created() {
        this.tableId = 'el-table_' + tableIdSeed++;
        this.debouncedUpdateLayout = debounce(50, () => this.doLayout());
      },
  
      mounted() {
        this.bindEvents();
        this.store.updateColumns();
        this.doLayout();
  
        this.resizeState = {
          width: this.$el.offsetWidth,
          height: this.$el.offsetHeight
        };
  
        // init filters
        this.store.states.columns.forEach(column => {
          if (column.filteredValue && column.filteredValue.length) {
            this.store.commit('filterChange', {
              column,
              values: column.filteredValue,
              silent: true
            });
          }
        });


        // 바디 상단 고정 헤더 체크
        console.log('startFixedBodyHeader')
        this.startFixedBodyHeader()

        this.$ready = true;
      },
  
      destroyed() {
        this.unbindEvents();

        /* 바디상단고정 체크 이벤트 해제*/
        $(window).off('scroll resize',this.checkFixedBodyHeader)
      },
  
      data() {
        const { hasChildren = 'hasChildren', children = 'children' } = this.treeProps;
        this.store = createStore(this, {
          rowKey: this.rowKey,
          defaultExpandAll: this.defaultExpandAll,
          selectOnIndeterminate: this.selectOnIndeterminate,
          // TreeTable 的相关配置
          indent: this.indent,
          lazy: this.lazy,
          lazyColumnIdentifier: hasChildren,
          childrenColumnName: children
        });
        const layout = new TableLayout({
          store: this.store,
          table: this,
          fit: this.fit,
          showHeader: this.showHeader,
          gutterWidth: 0
        });
        return {
          layout,
          isHidden: false,
          renderExpanded: null,
          resizeProxyVisible: false,
          resizeState: {
            width: null,
            height: null
          },
          // 是否拥有多级表头
          isGroup: false,
          scrollPosition: 'left',
          isFixedBodyHeader: false, // 바디 상단 고정헤더 실시간 활성 여부 
          tableContainerWidth: '100%' // 테이블 넓이 
        };
      }
    };
  </script>

  <style>
    .el-table--scrollable-y [data-scrollbar] .el-table__body-wrapper {
        height: auto !important;
        width: fit-content;
    }
    .el-table--scrollable-x [data-scrollbar] .el-table__body-wrapper {
      overflow-x: hidden;
      width: fit-content;
    }
    .el-table:not(.el-table--border) .el-table__header { border: 1px solid #e0e3e9; }

    /* 헤더 보더 지우기 */
    /*
    .el-table__fixed-header-wrapper th.el-table__cell, .el-table__header-wrapper th.el-table__cell {
      border: none;
    } 
    */   

    /* 헤더 그림자 처리 */
    .el-table__header-wrapper { transition: position 200ms linear;  box-shadow: 0px 3px 6px #e8e8e8;  position: relative; z-index: 4; overflow: hidden}
    /* 헤더 하단 보더 */
    .el-table__header th.el-table__cell.is-leaf { border-bottom: 1px solid #b2c5df; }

    /* 고정컬럼의 글씨가 겹쳐보이는 문제 해결*/
    .table-striped tbody tr.el-table__row:nth-of-type(odd) { background-color: #fafbfc }    

    /* 고정컬럼 */
    .el-table__fixed, .el-table__fixed-right { z-index: 4; }

    /* 고정컬럼 헤더 그림자 처리 */
    .el-table__fixed-header-wrapper { transition: position 200ms linear; z-index: 4;  box-shadow: 0px 3px 6px #e8e8e8; overflow: hidden}    
    .el-table:not(.header-body-fixed) .el-table__fixed-right .el-table__fixed-header-wrapper { width: auto !important }
    
    /* 헤더를 바디상단에 fix */
    /* 중간 헤더 */
    .header-body-fixed .el-table__header-wrapper { position: fixed; top: 0; overflow: hidden; }
    .header-body-fixed .el-table__fixed-body-wrapper { top:0 !important; }

    /* 왼쪽 헤더 */
    .header-body-fixed .el-table__fixed .el-table__fixed-header-wrapper { position: fixed; top: 0px; left: auto; overflow: hidden; }

    /* 오른쪽 헤더 */
    .header-body-fixed .el-table__fixed-right { z-index: 4; }
    .header-body-fixed .el-table__fixed-right .el-table__fixed-header-wrapper { position: fixed; top: 0px; right: auto; overflow: hidden; }
    .header-body-fixed .el-table__fixed-right .el-table__fixed-header-wrapper table.el-table__header { position: absolute; right: 0; }

</style>
  